import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { toArray, isEmpty } from 'lodash';
import { change, reset } from 'redux-form';

import { PATH } from '../../../constants/path';
import { CONTEXT_ROOT } from '../../../constants';
import { MODAL_TYPE_LOGGED_OUT } from '../../../services/modal/constants';
import { FLASHING_TICKET_ACCEPTED_FORMAT } from '../../../constants/form';

import { showModal } from '../../../services/modal/actions';
import { fetchPartnerShopsIfNeeded } from '../../../services/shop/actions';
import { logoutUser } from '../../../services/authentication/logout/actions';
import { setPartnerId, sendFlashingTicket } from '../../../services/flashing-ticket/actions';

import { hasOffline } from '../../../services/partner/utils';

import {
    FormComponent,
    SelectFieldComponent,
    LegendFieldComponent,
    FilesInputFieldComponent
} from '../../../components/form';
import HeadComponent from '../../HeadComponent';
import Breadcrumb from '../../breadcrumb/Breadcrumb';
import { withTemplate } from '../../template/withTemplate';
import ButtonComponent from '../../button/ButtonComponent';
import { PrimaryTemplate } from '../../template/PrimaryTemplate';
import { SuccessInfoComponent } from '../../form/SuccessInfoComponent';

import './FlashingTicketPage.scss';

const FORM_NAME = 'flashing-ticket';

export class FlashingTicketPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showSuccessMessage: false,
            mustResetFileField: false,
            error: ''
        };
    }

    componentWillMount() {
        this.checkAuthentication(this.props);
    }

    componentDidMount() {
        const parsed = queryString.parse(this.props.location.search);

        if (parsed && parsed.id) {
            this.props.setPartnerId(Number(parsed.id));
        }
    }

    componentDidUpdate(prevProps) {
        const { selectedPartnerId, fetchPartnerShopsIfNeeded, isAuthenticated } = this.props;
        const previousSelectedPartnerId = prevProps.selectedPartnerId;
        const { mustResetFileField } = this.state;

        if (prevProps.isAuthenticated !== isAuthenticated) {
            this.checkAuthentication(this.props);
        }

        if (selectedPartnerId !== previousSelectedPartnerId && selectedPartnerId) {
            fetchPartnerShopsIfNeeded(selectedPartnerId);
        }
        if (mustResetFileField) {
            this.setState({ mustResetFileField: false });
        }
    }

    checkAuthentication() {
        const { history, showModal, logoutUser, isAuthenticated } = this.props;

        if (!isAuthenticated) {
            new Promise(resolve => {
                history.push('/');
                resolve();
            }).then(() => logoutUser(() => showModal(MODAL_TYPE_LOGGED_OUT)));
        }
    }

    submit = data => {
        if (data.partnerId && data.storeId && data.file && !isEmpty(data.file)) {
            this.setState({ showSuccessMessage: true, error: null });
            this.props.sendFlashingTicket(data);
            setTimeout(() => {
                this.props.reset(FORM_NAME);
                this.setState({ showSuccessMessage: false, mustResetFileField: true });
            }, 30000);
        } else {
            this.setState({ error: 'Vous devez remplir tous les champs requis' });
        }
    };

    handlePartnerChange = e => {
        const { value } = e.target;
        this.props.setPartnerId(Number(value));
        this.props.change(FORM_NAME, 'storeId', null);
    };

    getShops = () => {
        const { shops, selectedPartnerId } = this.props;
        return toArray(shops[selectedPartnerId]);
    };

    render() {
        const { i18n, partners, requestSubmit, successSubmit, hasSubmitFailed, errorMessage } =
            this.props;
        const { showSuccessMessage, mustResetFileField, error } = this.state;
        const parsed = queryString.parse(this.props.location.search);

        return (
            <main role="main" className="conteneur conteneur-mobile" id="p-flashing-ticket">
                <HeadComponent
                    title={i18n.TITLE}
                    description={i18n.DESCRIPTION}
                    canonicalLink={`${CONTEXT_ROOT}${PATH.FLASHING_TICKET_PAGE}`}
                />
                <Breadcrumb
                    defaultRoute
                    routes={[
                        {
                            label: i18n.BREADCRUMB,
                            route: PATH.FLASHING_TICKET_PAGE
                        }
                    ]}
                />
                <h1>{i18n.TITLE}</h1>

                <p className="intro">{i18n.TEXT1}</p>

                <FormComponent
                    form={FORM_NAME}
                    className="flashing-ticket-form"
                    classNameInputContainer="grid-2 has-gutter"
                    encType
                    initialValues={parsed && parsed.id ? { partnerId: parsed.id } : {}}
                    onSubmit={this.submit}
                    requestSubmit={requestSubmit}
                    errorSubmit={hasSubmitFailed}
                    submitText={i18n.BUTTON_SUBMIT}
                    hideButton>
                    <SelectFieldComponent
                        name="partnerId"
                        label={i18n.PARTNER_SELECT_LABEL}
                        classNameInputContainer="one-half mr50"
                        defaultOptionLabel={i18n.PARTNER_SELECT_LABEL}
                        options={partners.map(p => ({
                            label: p.name,
                            value: p.id
                        }))}
                        onChange={this.handlePartnerChange}
                        isRequired
                    />
                    <SelectFieldComponent
                        name="storeId"
                        label={i18n.STORE_SELECT_LABEL}
                        classNameInputContainer="one-half mr50"
                        defaultOptionLabel={
                            this.getShops().length === 0
                                ? i18n.NO_STORE_AVAILABLE
                                : i18n.STORE_SELECT_LABEL
                        }
                        options={this.getShops().map(s => ({
                            label: `${s.name}`,
                            value: s.id
                        }))}
                        isDisabled={this.getShops().length === 0}
                        isRequired
                    />
                    <LegendFieldComponent classNameInputContainer="full file-label">
                        {i18n.FILE_LABEL}
                    </LegendFieldComponent>
                    <LegendFieldComponent classNameInputContainer="full file-label-hint">
                        {i18n.FILE_LABEL_HINT}
                    </LegendFieldComponent>
                    <FilesInputFieldComponent
                        name="file"
                        nameForm={FORM_NAME}
                        classNameInputContainer="full file-input"
                        btText={i18n.FILE_BUTTON_TEXT}
                        formatAccepted={FLASHING_TICKET_ACCEPTED_FORMAT}
                        maxSize={2}
                        minRequired={1}
                        reset={mustResetFileField}
                    />
                    <LegendFieldComponent classNameInputContainer="full">
                        {i18n.MANDATORY_FIELDS}
                    </LegendFieldComponent>
                    <LegendFieldComponent classNameInputContainer="full delay">
                        {i18n.WARNING}
                    </LegendFieldComponent>
                    <LegendFieldComponent classNameInputContainer="full button-wrapper">
                        {!showSuccessMessage && (
                            <ButtonComponent type="submit" color="main" text={i18n.BUTTON_SUBMIT} />
                        )}

                        {showSuccessMessage &&
                            successSubmit &&
                            !requestSubmit &&
                            !hasSubmitFailed && (
                                <SuccessInfoComponent>
                                    <p>{i18n.SUBMIT_SUCCEEDED}</p>
                                </SuccessInfoComponent>
                            )}
                        {error && !showSuccessMessage && !hasSubmitFailed && (
                            <LegendFieldComponent classNameInputContainer="color-err">
                                {error}
                            </LegendFieldComponent>
                        )}
                        {hasSubmitFailed && (
                            <p>
                                <LegendFieldComponent classNameInputContainer="color-err">
                                    {errorMessage}
                                </LegendFieldComponent>
                            </p>
                        )}
                    </LegendFieldComponent>
                </FormComponent>
            </main>
        );
    }
}

FlashingTicketPage.propTypes = {
    i18n: PropTypes.object.isRequired
};

const mapStateToProps = state => {
    return {
        i18n: state.i18n.page.FLASHING_TICKET,
        isAuthenticated: state.authentication.isAuthenticated,
        location: state.routing.location,
        selectedPartnerId: state.flashingTicket.partnerId,
        partners: toArray(state.partner.partners).filter(p => hasOffline(p)),
        shops: state.shop.shops,
        requestSubmit: state.flashingTicket.fetching,
        successSubmit: state.flashingTicket.successFlashingTicketSubmit,
        hasSubmitFailed: !isEmpty(state.flashingTicket.error),
        errorMessage: state.flashingTicket.error
    };
};

const mapDispatchToProps = {
    reset,
    change,
    setPartnerId,
    sendFlashingTicket,
    fetchPartnerShopsIfNeeded,
    logoutUser,
    showModal
};

export default withTemplate(
    connect(mapStateToProps, mapDispatchToProps)(FlashingTicketPage),
    PrimaryTemplate
);



// WEBPACK FOOTER //
// ./src/components/pages/flashing-ticket/FlashingTicketPage.js